import { GridSize } from '@mui/material/Grid';
import Grid from '@mui/material/Grid2';
import { isArray } from 'lodash';
import React, { ReactNode, useMemo } from 'react';
import { useMeasure } from 'react-use';
import { AccidentsWidgetConnected, NewAccidentWidgetConnected } from './accidents';
import { RecentAccidentsWidgetConnected } from './accidents/RecentAccidentsWidget';
import { PageLayout } from './common';
import { useAuth } from './hooks';
import { useDateRange } from './hooks/useDateRange';
import { RentalWidgetConnected } from './rental';
import { MerchantStatisticWidgetConnected, PeriodProgressWidgetConnected } from './statisctics';
import { AccidentsTrendWidgetConnected } from './statisctics/AccidentsTrendWidget';
import { DashboardMonthSelect } from './statisctics/DashboardMonthSelect';
import { DashboardRangeProvider } from './statisctics/DashboardRangeContext';
import { FuelWidgetConnected } from './statisctics/FuelWidget';
import { TopMerchantStatisticWidgetConnected } from './statisctics/TopMerchantStatisticWidget';
import { WeekActivityWidgetConnected } from './statisctics/WeekActivityWidget';
import { Role } from './types';

const DASHBOARD_BREAKPOINTS = {
  SM: 550,
  MD: 1100
};

type DashboardLayout = 'small' | 'medium' | 'large';

//GriddedWidget = [component, space to take up in a grid]
type GriddedWidget = [React.ReactNode, GridSize | null | undefined, Role | undefined] | React.ReactNode;

const widgetLayout: Record<DashboardLayout, GriddedWidget[]> = {
  small: [
    [<NewAccidentWidgetConnected />, 12, Role.Dealer],
    [<RentalWidgetConnected />, 12, Role.Dealer],
    [<AccidentsWidgetConnected />, 12],
    [<MerchantStatisticWidgetConnected />, 12, Role.Dealer],
    [<PeriodProgressWidgetConnected />, 12, Role.Dealer],
    [<WeekActivityWidgetConnected />, 12, Role.Dealer],
    [<AccidentsTrendWidgetConnected />, 12, Role.Dealer],
    [<TopMerchantStatisticWidgetConnected />, 12, Role.Dealer],
    [<FuelWidgetConnected />, 12, Role.Dealer],
    [<RecentAccidentsWidgetConnected />, 12, Role.Dealer]
  ],
  medium: [
    [<NewAccidentWidgetConnected />, 6, Role.Dealer],
    [<RentalWidgetConnected />, 6, Role.Dealer],
    [<AccidentsWidgetConnected />, 6],
    [<TopMerchantStatisticWidgetConnected />, 6, Role.Dealer],
    [<MerchantStatisticWidgetConnected />, 12, Role.Dealer],
    [<PeriodProgressWidgetConnected />, 12, Role.Dealer],
    [<AccidentsTrendWidgetConnected />, 12, Role.Dealer],
    [<WeekActivityWidgetConnected />, 12, Role.Dealer],
    [<FuelWidgetConnected />, 12, Role.Dealer],
    [<RecentAccidentsWidgetConnected />, 12, Role.Dealer]
  ],
  large: [
    [<NewAccidentWidgetConnected />, 6, Role.Dealer],
    [<RentalWidgetConnected />, 6, Role.Dealer],
    [<MerchantStatisticWidgetConnected />, 8, Role.Dealer],
    [<AccidentsWidgetConnected />, 4],
    [<PeriodProgressWidgetConnected />, 12, Role.Dealer],
    [<AccidentsTrendWidgetConnected />, 8, Role.Dealer],
    [<WeekActivityWidgetConnected />, 4, Role.Dealer],
    [<FuelWidgetConnected />, 8, Role.Dealer],
    [<TopMerchantStatisticWidgetConnected />, 4, Role.Dealer],
    [<RecentAccidentsWidgetConnected />, 12, Role.Dealer]
  ]
};

const getDashboardLayout = (width: number): DashboardLayout => {
  if (width < DASHBOARD_BREAKPOINTS.SM) return 'small';
  if (width < DASHBOARD_BREAKPOINTS.MD) return 'medium';
  return 'large';
};

export const DashboardPage = () => {
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const isContainerReady = width > 0;
  const layout = useMemo(() => getDashboardLayout(width), [width]);
  const { user } = useAuth();
  const { range, rangeDates, setMonth } = useDateRange({
    weekStartsOn: 1,
    initialPreset: 'thisMonth',
    fmt: 'yyyy-MM-dd'
  });

  const container = (content: ReactNode) => (
    <PageLayout
      ref={measureRef}
      title="Nástěnka"
      extra={user?.role === Role.Dealer && <DashboardMonthSelect value={rangeDates[0]} onChange={setMonth} />}
    >
      {content}
    </PageLayout>
  );

  if (!isContainerReady) return container(null);

  return container(
    <DashboardRangeProvider range={range}>
      <Grid container spacing={2}>
        {widgetLayout[layout].map((widget, widgetIdx) => {
          const [widgetComponent, size, roleAllowed] = !isArray(widget) ? [widget, 12] : widget;
          if (roleAllowed && user?.role !== roleAllowed) return null;
          return (
            <Grid size={{ xs: size }} key={widgetIdx}>
              {widgetComponent}
            </Grid>
          );
        })}
      </Grid>
    </DashboardRangeProvider>
  );
};
